import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { SectionHeader } from '@/ui/molecules/section-header/SectionHeader'
import { BarChart3, Eye, RefreshCw, Shield, Sparkles, TrendingUp } from 'lucide-react'

const FEATURES = [
  {
    icon: TrendingUp,
    title: 'Optimized Yields',
    description: 'Strategies continuously rebalanced across DeFi to maintain efficiency and maximize returns.',
  },
  {
    icon: Shield,
    title: 'Institutional-Grade Risk',
    description:
      'Curated by Clearstar with conservative risk tolerance, rigorous monitoring, and prudent strategy management.',
  },
  {
    icon: Eye,
    title: 'Non-Custodial Safety',
    description: 'Risk curators can rebalance allocations but cannot remove user funds. Your keys, your crypto.',
  },
  {
    icon: RefreshCw,
    title: 'Passive & Simple',
    description: 'Deposit once and earn aggregated yield. No need to manage individual lending or borrowing positions.',
  },
]

const HOW_IT_WORKS = [
  {
    step: 1,
    title: 'Deposit',
    description: 'Choose a vault and deposit your assets through a simple interface.',
    color: '#A1FCE7',
  },
  {
    step: 2,
    title: 'Strategies Deploy',
    description: 'Clearstar allocates deposits across curated DeFi and lending strategies on HypurrFi Markets.',
    color: '#FCE572',
  },
  {
    step: 3,
    title: 'Earn Yield',
    description: 'Yield is generated passively and compounds. Withdraw anytime.',
    gradient: true,
  },
]

export function EarnFeatureCards() {
  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      {/* How it works */}
      <div className="flex flex-1 flex-col gap-3">
        <SectionHeader title="How it works" icon={<Sparkles className="h-4 w-4 text-[#FBE572]" />} />

        <div className="grid gap-3 sm:grid-cols-3">
          {HOW_IT_WORKS.map((item, i) => (
            <Panel.Wrapper
              key={item.step}
              className="p-4"
              style={{
                animationDelay: `${i * 0.1 + 0.1}s`,
                animationName: 'sweep-card-in',
                animationDuration: '0.4s',
                animationTimingFunction: 'ease-out',
                animationFillMode: 'forwards',
                opacity: 0,
              }}
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div
                    className="flex h-6 w-6 items-center justify-center rounded-full font-bold text-xs"
                    style={{
                      background: item.gradient ? 'linear-gradient(135deg, #FBE572, #A1FCE7)' : `${item.color}20`,
                      color: item.gradient ? '#0E0E10' : item.color,
                    }}
                  >
                    {item.step}
                  </div>
                  <Typography
                    variant="h4"
                    className="font-semibold text-sm"
                    style={
                      item.gradient
                        ? {
                            background: 'linear-gradient(90deg, #FCE572, #A1FCE7)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }
                        : { color: item.color }
                    }
                  >
                    {item.title}
                  </Typography>
                </div>
                <Typography variant="p" className="text-muted-foreground text-xs leading-relaxed">
                  {item.description}
                </Typography>
              </div>
            </Panel.Wrapper>
          ))}
        </div>
      </div>

      {/* Why Earn Vaults */}
      <div className="flex w-full flex-col gap-3 lg:w-72">
        <SectionHeader title="Why Earn Vaults" icon={<BarChart3 className="h-4 w-4 text-[#A1FCE7]" />} />

        {FEATURES.map((feature, i) => (
          <Panel.Wrapper
            key={feature.title}
            className="p-4"
            style={{
              animationDelay: `${i * 0.1 + 0.2}s`,
              animationName: 'sweep-card-in',
              animationDuration: '0.4s',
              animationTimingFunction: 'ease-out',
              animationFillMode: 'forwards',
              opacity: 0,
            }}
          >
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#FBE572]/10 to-[#A1FCE7]/10">
                <feature.icon className="h-4 w-4 text-[#A1FCE7]" />
              </div>
              <div>
                <Typography variant="h4" className="font-semibold text-sm">
                  {feature.title}
                </Typography>
                <Typography variant="p" className="mt-1 text-muted-foreground text-xs leading-relaxed">
                  {feature.description}
                </Typography>
              </div>
            </div>
          </Panel.Wrapper>
        ))}

        {/* Clearstar curator card */}
        <Panel.Wrapper className="overflow-hidden p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#FBE572]/20 to-[#A1FCE7]/20">
              <Shield className="h-4 w-4 text-[#FBE572]" />
            </div>
            <div>
              <Typography variant="h4" className="font-semibold text-sm">
                Curated by Clearstar
              </Typography>
              <Typography variant="p" className="mt-1 text-muted-foreground text-xs leading-relaxed">
                Institutional-grade strategies across decentralized markets, underpinned by rigorous risk management.
              </Typography>
              <a
                href="https://www.clearstar.xyz/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs transition-colors"
                style={{
                  background: 'linear-gradient(90deg, #FCE572, #A1FCE7)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Learn more about Clearstar
                <span style={{ WebkitTextFillColor: '#A1FCE7' }}>&rarr;</span>
              </a>
            </div>
          </div>
        </Panel.Wrapper>
      </div>
    </div>
  )
}
